import { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Search, Menu, X } from "lucide-react";
import { Logo, SquareLogo } from "@/components/shared/Logo";
import { teamDropdown, moreDropdown } from "@/data/mockData";

// Routes are real React Router pages.
// Anchors (with #) only work on the homepage; from any other page they link
// back to the home route + hash so the browser scrolls correctly.
const navLinks = [
  { label: "Home", to: "/", kind: "route" },
  { label: "News", to: "/news", kind: "route" },
  { label: "Articles", to: "/articles", kind: "route" },
  // { label: "Cohort", to: "/cohort", kind: "route" },
  // { label: "Events", to: "/#events", kind: "anchor" },
  { label: "RFT", to: "/#rft", kind: "anchor" },
  // { label: "Contact", to: "/contact", kind: "route" },
  // { label: "Advertise", to: "/#advertise", kind: "anchor" },
];

export const Navbar = ({ onSearchClick }) => {
  const [open, setOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const closeTimers = useRef({});
  const { pathname } = useLocation();

  const openDropdown = (key, setter) => {
    window.clearTimeout(closeTimers.current[key]);
    setter(true);
  };

  const closeDropdown = (key, setter) => {
    window.clearTimeout(closeTimers.current[key]);
    closeTimers.current[key] = window.setTimeout(() => setter(false), 180);
  };

  const renderLink = (l, mobile = false) => {
    const isActive =
      l.kind === "route" &&
      (pathname === l.to || (l.to !== "/" && pathname.startsWith(l.to)));
    const baseClass = mobile
      ? "block hover:text-flare"
      : `hover:text-flare transition-colors ${isActive ? "text-flare" : ""}`;
    if (l.kind === "route") {
      return (
        <Link
          key={l.label}
          to={l.to}
          data-testid={`${mobile ? "mobile-" : ""}nav-link-${l.label.toLowerCase()}`}
          className={baseClass}
          onClick={() => mobile && setOpen(false)}
        >
          {l.label}
        </Link>
      );
    }
    return (
      <Link
        key={l.label}
        to={l.to}
        data-testid={`${mobile ? "mobile-" : ""}nav-link-${l.label.toLowerCase()}`}
        className={baseClass}
        onClick={() => mobile && setOpen(false)}
      >
        {l.label}
      </Link>
    );
  };

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050505]/80 border-b border-white/10"
    >
      <div className="px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          aria-label="Startup Unplugged — Home"
          className="flex flex-row items-center gap-2"
        >
          <SquareLogo className="h-12 md:h-16" testid="navbar-brand-logo" />{" "}
          <p className="font-display font-extrabold text-lg md:text-xl tracking-tighter">
            Startup<span className="text-flare">Unplugged</span>
            <span className="text-flare">.</span>
          </p>
        </Link>

        {/* Desktop menu */}
        <div className="hidden lg:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-bone">
          {navLinks.map((l) => renderLink(l))}

          {/* Contributors dropdown */}
          <div
            className="relative"
            onMouseEnter={() => openDropdown("team", setTeamOpen)}
            onMouseLeave={() => closeDropdown("team", setTeamOpen)}
          >
            {/* <button
              data-testid="nav-team-dropdown"
              onClick={() => setTeamOpen((current) => !current)}
              aria-expanded={teamOpen}
              className="flex items-center gap-1 hover:text-flare transition-colors"
            >
              CONTRIBUTORS <ChevronDown className="w-3 h-3" />
            </button> */}
            {teamOpen && (
              <div className="absolute top-full left-0 pt-3">
                <div className="bg-black border border-white/10 min-w-[200px] py-2">
                  {teamDropdown.map((t) => (
                    <Link
                      key={t.label}
                      to={`/${t.href}`}
                      data-testid={`nav-team-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="block px-4 py-2.5 hover:bg-white/5 hover:text-flare transition-colors"
                      onClick={() => setTeamOpen(false)}
                    >
                      {t.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em]">
          <button
            data-testid="nav-search-btn"
            onClick={onSearchClick}
            className="p-2 hover:text-flare transition-colors"
            aria-label="Search"
          >
            <Search className="w-4 h-4" />
          </button>
          <Link
            to="/#newsletter"
            data-testid="nav-subscribe-btn"
            className="bg-flare text-black px-4 py-2 hover:bg-white transition-colors"
          >
            Subscribe
          </Link>
          <div
            className="relative"
            onMouseEnter={() => openDropdown("more", setMoreOpen)}
            onMouseLeave={() => closeDropdown("more", setMoreOpen)}
          >
            <button
              data-testid="nav-more-dropdown"
              onClick={() => setMoreOpen((current) => !current)}
              aria-expanded={moreOpen}
              className="flex items-center gap-1 hover:text-flare transition-colors"
            >
              More <ChevronDown className="w-3 h-3" />
            </button>
            {moreOpen && (
              <div className="absolute top-full right-0 pt-3">
                <div className="bg-black border border-white/10 min-w-[220px] py-2">
                  {moreDropdown.map((m) => (
                    <Link
                      key={m.label}
                      to={`/${m.href}`}
                      data-testid={`nav-more-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="block px-4 py-2.5 hover:bg-white/5 hover:text-flare transition-colors"
                      onClick={() => setMoreOpen(false)}
                    >
                      {m.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-black border-t border-white/10 px-6 py-6 font-mono uppercase tracking-[0.15em] text-sm space-y-4"
        >
          {navLinks.map((l) => renderLink(l, true))}
          <div className="pt-3 border-t border-white/10 space-y-3">
            <p className="text-bone-muted text-[10px]">More</p>
            {moreDropdown
              .filter((m) =>
                ["Partner With Us", "Advertise", "Contact"].includes(m.label),
              )
              .map((m) => (
                <Link
                  key={m.label}
                  to="/contact"
                  className="block text-sm hover:text-flare"
                  onClick={() => setOpen(false)}
                >
                  — {m.label}
                </Link>
              ))}
          </div>
          <Link
            to="/#newsletter"
            data-testid="mobile-subscribe-btn"
            className="inline-block bg-flare text-black px-5 py-2.5 mt-2"
            onClick={() => setOpen(false)}
          >
            Subscribe
          </Link>
        </div>
      )}
    </nav>
  );
};
